import React, { useState, useEffect } from "react";
import { CloudUpload, X } from "lucide-react";
import { useApiCall } from "../custom-hooks/api-call";

const FilesTab = ({ leadId, attachmenttype }) => {
  const apiCall = useApiCall();

  const [pendingUploads, setPendingUploads] = useState([]); 
  const [uploaded, setUploaded] = useState([]); 
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

 
  const fetchAttachments = async () => {
    try {
      setIsLoading(true);
      const res = await apiCall({
        method: "post",
        url: "attachments/get-attachments",
        data: {
          attachable_id: leadId,
          attachable_type: attachmenttype,
        },
      });
      if (res?.status && Array.isArray(res.data)) {
        setUploaded(res.data);
      }
    } catch (err) {
      console.error("Fetch attachments error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) fetchAttachments();
  }, [leadId]);

 
  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    const formData = new FormData();
    newFiles.forEach((f) => formData.append("files[]", f));

    try {
      const uploadRes = await apiCall({
        method: "post",
        url: "attachments/upload",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (uploadRes?.data?.length) {
        setPendingUploads((prev) => [...uploadRes.data, ...prev]);
      }
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      e.target.value = ""; // reset file input
    }
  };

  // Handle drag & drop upload
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (!droppedFiles.length) return;

    const formData = new FormData();
    droppedFiles.forEach((f) => formData.append("files[]", f));

    try {
      const uploadRes = await apiCall({
        method: "post",
        url: "attachments/upload",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (uploadRes?.data?.length) {
        setPendingUploads((prev) => [...uploadRes.data, ...prev]);
      }
    } catch (err) {
      console.error("Upload error:", err);
    }
  };

  // Delete pending unconfirmed upload
  const removePendingUpload = async (id) => {
    try {
      await apiCall({
        method: "post",
        url: `attachments/delete-unconfirmed-attachment/${id}`,
      });
      setPendingUploads((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete unconfirmed file:", err);
    }
  };

  // Confirm all pending uploads
  const handleConfirmPending = async () => {
    if (!pendingUploads.length) return;
    setIsConfirming(true);
    try {
      const ids = pendingUploads.map((p) => p.id);
      const res = await apiCall({
        method: "post",
        url: "attachments/confirm",
        data: {
          attachment_ids: ids,
          attachable_type: attachmenttype,
          attachable_id: leadId,
        },
      });

      // Move pending to uploaded
      setUploaded((prev) => [...pendingUploads, ...prev]);
      setPendingUploads([]);
      fetchAttachments()
    } catch (err) {
      console.error("Confirm error:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Box */}
      <div
        onClick={() => document.getElementById("fileInput").click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition cursor-pointer ${isDragging
          ? "border-black bg-gray-100"
          : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          }`}
      >
        <CloudUpload className="w-10 h-10 text-black mb-3" />
        <p className="text-gray-700 text-sm mb-2">
          Drag & drop files here or click to select
        </p>

        <input
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          id="fileInput"
        />

        {/* Pending uploads */}
        {pendingUploads.length > 0 && (
          <div className="mt-4 w-full max-w-sm">
            <div className="text-xs text-gray-700 mb-2 font-semibold">
              Pending uploads
            </div>
            <ul className="text-xs text-gray-700 text-left space-y-2">
              {pendingUploads.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-md"
                >
                  <span className="truncate max-w-[150px]">{p.file_name}</span>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      await removePendingUpload(p.id);
                    }}
                    className="text-gray-500 hover:text-red-600 transition"
                    title="Remove pending upload"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex gap-2 mt-3">
              <button
                onClick={async (e) => {
                  e.stopPropagation(); 
                  await handleConfirmPending(); 
                }}
                disabled={isConfirming}
                className="flex-1 bg-black text-white text-sm px-4 py-2 rounded-md hover:bg-black-600"
              >
                {isConfirming ? "Confirming..." : "Confirm Pending"}
              </button>
            </div>

          </div>
        )}
      </div>

      {/* Confirmed / Uploaded Files */}
      {isLoading ? (
        <p className="text-gray-400 text-sm text-center">Loading files...</p>
      ) : uploaded.length > 0 ? (
        <div className="divide-y divide-gray-200 border-t">
          {uploaded.map((file) => {
            const isImage = /\.(png|jpg|jpeg|webp|gif)$/i.test(file.file_name);
            const isPDF = /\.pdf$/i.test(file.file_name);
            const isText = /\.(txt|md|csv)$/i.test(file.file_name);
            const isDoc = /\.(docx?|pptx?|xlsx?)$/i.test(file.file_name);

            const getFileIcon = () => {
              if (isPDF)
                return (
                  <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-1 rounded">
                    PDF
                  </span>
                );
              if (isDoc)
                return (
                  <span className="bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded">
                    DOC
                  </span>
                );
              if (isText)
                return (
                  <span className="bg-amber-100 text-amber-600 text-xs font-semibold px-2 py-1 rounded">
                    TXT
                  </span>
                );
              return (
                <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-1 rounded">
                  FILE
                </span>
              );
            };

            const size = file.size ? `${(file.size / 1024).toFixed(1)} KB` : "";

            return (
              <div
                key={file.id}
                className="flex items-center justify-between py-3"
              >
                <div className="flex items-center gap-3">
                  {isImage ? (
                    <img
                      src={file.file_url}
                      alt={file.file_name}
                      className="w-10 h-10 rounded-md object-cover border"
                    />
                  ) : (
                    getFileIcon()
                  )}
                  <div className="flex flex-col">
                    <a
                      href={file.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-black hover:underline truncate max-w-[180px]"
                    >
                      {file.file_name}
                    </a>
                    {file.created_at && (
                      <span className="text-xs text-gray-400">
                        {file.created_at}
                      </span>
                    )}
                  </div>
                </div>
                {/* <div className="text-xs text-gray-500 min-w-[60px] text-right">
                  {size || "—"}
                </div> */}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 text-sm text-center">
          No files uploaded yet.
        </p>
      )}
    </div>
  );
};

export default FilesTab;
